import { Trans } from '@lingui/macro'
import { StartOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, ButtonProps, Typography } from '@mui/material'
import Inquiry from 'app/EgretLayout/SharedCompoents/Inquiry'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import { useExpressionOfInterestStatus } from 'app/hooks/useExpressionOfInterestStatus'
import { usePrequalifications } from 'app/hooks/usePrequalifications'
import { ReduxOrganization, ReduxState } from 'app/redux/ReduxInterfaces'
import { ButtonIcon } from 'app/views/common-components/ButtonIcon'
import { useFormContext } from 'app/views/forms/form-page/FormContext'
import { ExpressionOfInterestBanner } from 'app/views/funding/ExpressionOfInterest'
import { FUNDING_PATHS } from 'app/views/funding/FundingConstants'
import { InvalidProvinceAlert } from 'app/views/funding/FundingStreamQuiz'
import {
  PrequalificationFundBanner,
  prequalificationStageToMessages
} from 'app/views/grants/applications/old-application/PrequalificationFundBanner'
import { useSnackbar } from 'notistack'
import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ACTION_BUTTON_ACTIONS } from './FormEditorActionButton'

interface FormActionButtonTypeProps {
  variant?: ButtonProps['variant']
  color?: ButtonProps['color']
  action?: keyof typeof ACTION_BUTTON_ACTIONS
  parameter?: string
}

interface FormActionButtonProps {
  title: React.ReactNode
  typeProps: FormActionButtonTypeProps
  preview?: boolean
  disabled?: boolean
}

export const FormActionButton = ({
  title,
  typeProps,
  preview,
  disabled: disabledProp
}: FormActionButtonProps) => {
  const currentOrganization = useSelector<ReduxState, ReduxOrganization>(
    (state) => state.organization
  )
  const { fundingStreams } = useGlobalQueries()
  const { variant, color, action, parameter } = typeProps
  const { actionHandlers } = useFormContext()
  const { prequalificationsByStream, query: prequalificationsQuery } = usePrequalifications()
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()

  const [expressionOfInterestOpen, setExpressionOfInterestOpen] = React.useState(false)

  const cancelDefaultAction = false
  const label: React.ReactNode = title
  const fundingStreamInfo = parameter ? fundingStreams.find((fs) => fs.id === parameter) : undefined

  const invalidProvince =
    Array.isArray(fundingStreamInfo?.disableInProvinces) &&
    fundingStreamInfo?.disableInProvinces.includes(currentOrganization.province)
  let disabled = disabledProp || invalidProvince

  const isPrequalification = Boolean(fundingStreamInfo?.prequalificationEligibilityQuiz)
  const requiresExpressionOfInterest = Boolean(fundingStreamInfo?.requiresExpressionOfInterest)
  const {
    query: expressionOfInterestQuery,
    approvedCaseExists,
    unresolvedCaseExists
  } = useExpressionOfInterestStatus(fundingStreamInfo?.caseType)

  const existingPrequalifications = parameter ? (prequalificationsByStream[parameter] ?? []) : []

  let buttonComponent = (
    <Button
      fullWidth
      disabled={disabled}
      color={color ?? 'primary'}
      variant={variant ?? 'contained'}
      onClick={() => {
        if (action) {
          if (preview) {
            enqueueSnackbar(
              `This will call ${ACTION_BUTTON_ACTIONS[action].label} with parameter ${parameter}`,
              { variant: 'info' }
            )
          } else if (action in actionHandlers) {
            if (cancelDefaultAction && prequalificationData != null) {
              history.push(FUNDING_PATHS.PREQUALIFICATION + `/${prequalificationData.id}`)
            } else {
              actionHandlers[action](parameter)
            }
          }
        }
      }}
    >
      <Typography>{label}</Typography>
      <ButtonIcon IconClass={StartOutlined} />
    </Button>
  )
  const prequalificationData = isPrequalification ? existingPrequalifications[0] : undefined

  if (isPrequalification) {
    const messages =
      prequalificationData != null && prequalificationStageToMessages[prequalificationData.stage]
    if (prequalificationData != null) {
      disabled = !messages.redirect && !messages.canStart
    }

    buttonComponent = (
      <LoadingButton
        fullWidth
        disabled={disabled}
        loading={prequalificationsQuery.isInitialLoading}
        color={color ?? 'primary'}
        variant={variant ?? 'contained'}
        onClick={() => {
          if (action) {
            if (preview) {
              enqueueSnackbar(
                `This will call ${ACTION_BUTTON_ACTIONS[action].label} with parameter ${parameter}`,
                { variant: 'info' }
              )
            } else if (action in actionHandlers) {
              if (messages?.redirect && prequalificationData != null) {
                history.push(FUNDING_PATHS.PREQUALIFICATION + `/${prequalificationData.id}`)
              } else {
                actionHandlers[action](parameter)
              }
            }
          }
        }}
      >
        <Typography>
          {prequalificationData != null ? messages.button : <Trans>START_PREQUALIFICATION</Trans>}
        </Typography>
        <ButtonIcon IconClass={StartOutlined} />
      </LoadingButton>
    )
  } else if (requiresExpressionOfInterest) {
    buttonComponent = (
      <LoadingButton
        fullWidth
        disabled={unresolvedCaseExists || disabled}
        loading={expressionOfInterestQuery.isInitialLoading}
        color={color ?? 'primary'}
        variant={variant ?? 'contained'}
        onClick={() => {
          if (action) {
            if (preview) {
              enqueueSnackbar(
                `This will call ${ACTION_BUTTON_ACTIONS[action].label} with parameter ${parameter}`,
                { variant: 'info' }
              )
            } else if (action in actionHandlers) {
              if (requiresExpressionOfInterest && !approvedCaseExists) {
                setExpressionOfInterestOpen(true)
              } else if (approvedCaseExists) {
                actionHandlers[action](parameter)
              }
            }
          }
        }}
      >
        <Typography>
          {unresolvedCaseExists ? (
            <Trans>EXPRESSION_OF_INTEREST_IN_PROGRESS</Trans>
          ) : approvedCaseExists ? (
            <Trans>EXPRESSION_OF_INTEREST_APPROVED</Trans>
          ) : (
            <Trans>EXPRESS_INTEREST</Trans>
          )}
        </Typography>
        <ButtonIcon IconClass={StartOutlined} />
      </LoadingButton>
    )
  }

  return (
    <div>
      {buttonComponent}
      {invalidProvince && (
        <Box sx={{ marginTop: 1 }}>
          <InvalidProvinceAlert />
        </Box>
      )}
      {requiresExpressionOfInterest && !invalidProvince && (
        <Box sx={{ marginTop: '8px' }}>
          <ExpressionOfInterestBanner
            unresolvedCaseExists={unresolvedCaseExists}
            approvedCaseExists={approvedCaseExists}
          />
          <Inquiry
            open={expressionOfInterestOpen}
            handleClose={() => {
              setExpressionOfInterestOpen(false)
              expressionOfInterestQuery.refetch()
            }}
            selectCaseLocation
            additionalValues={{
              skipAssigment: true,
              subType: 'Expression of interest'
            }}
            translations={{
              titleTranslation: <Trans>EXPRESS_INTEREST</Trans>,
              subjectFieldLabel: <Trans>EXPRESS_INTEREST_SUBJECT</Trans>,
              describeTranslation: <Trans>EXPRESS_INTEREST_DESCRIPTION</Trans>
            }}
            caseType={fundingStreamInfo?.caseType}
          />
        </Box>
      )}
      {prequalificationData != null && !invalidProvince && (
        <Box sx={{ marginTop: '8px' }}>
          <PrequalificationFundBanner currentStage={prequalificationData.stage} />
        </Box>
      )}
    </div>
  )
}
